import {
  Accordion,
  AccordionItem,
  AccordionProps,
  Icon,
} from '@toggle/design-system';
import type { HighConvictionReport } from '@toggle/toggle';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './AssessmentAccordion.module.scss';

export interface AssessmentAccordionProps
  extends Omit<AccordionProps, 'items'> {
  title: string;
  items: Partial<HighConvictionReport>;
  showHasValue?: boolean;
}

export const AssessmentAccordion = ({
  items,
  title,
  showHasValue = false,
}: AssessmentAccordionProps) => {
  const { t } = useTranslation('common');

  const AssessmentItems = useMemo((): AccordionItem[] => {
    return Object.entries(items).map(([qualityKey, value]) => ({
      /* i18next-extract-disable */
      hideDefaultTitleIcon: true,
      title: (
        <span className={styles.titleRow}>
          <span className={styles.criterionTitle}>
            {t(`common:qualityChecklist.labels.${qualityKey}`)}
          </span>
        </span>
      ),
      content: t(`common:qualityChecklist.tooltips.${qualityKey}`),
      value: (
        <div className={styles.valueStarCluster}>
          {value ? (
            <p
              className={clsx(
                styles.explicitValue,
                styles.explicitValueBullish
              )}
            >
              {t(`common:qualityChecklist.labels.yes`)}
            </p>
          ) : (
            <p className={styles.explicitValue}>
              {t(`common:qualityChecklist.labels.no`)}
            </p>
          )}
          <span className={styles.pipeSeparator} aria-hidden />
          <Icon
            iconName={value ? 'StarFill' : 'StarEmpty'}
            fillColor={value ? 'var(--icon-primary)' : 'var(--icon-default)'}
            size={18}
          />
        </div>
      ),
      showHasValue,
    }));
  }, [items, showHasValue, t]);

  return (
    <div className={styles.revampAssessmentResultWrapper}>
      <h2
        className={styles.resultTitle}
        data-testid="assessment-accordion-title"
      >
        {title}
      </h2>
      <Accordion
        className={styles.accordion}
        items={AssessmentItems}
        variant="secondary"
        collapseAccordionsOnClick={false}
      />
    </div>
  );
};
